import { IBufferControllerConfig } from '@bufferController/BufferController.config';
import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics } from '@bufferController/BufferMetrics/IBufferMetrics';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { IVODFragment } from '@streamLoader/VODPlaylistRotationController/VODPlaylistRotationController';
import bind from '@utils/decorators/bind';

interface IVodFragmentTiming {
  startTs: number;
  endTs: number;
}
export class VODGapController extends ConfigConsumer<IBufferControllerConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = {};
  public groups: string[] = ['buffer'];

  private _videoElement: HTMLVideoElement | null = null;
  private _fragments: IVodFragmentTiming[] = [];
  private _lastPlaybackPosition: number = 0;
  private _numberOfDetectedStallCandidates: number = 0;

  private readonly _onBufferMetricsReadyListener: string;
  private readonly _onVODFragmentInfoAvailableListener: string;

  constructor(eventBus: TEventBus) {
    super('bufferController', eventBus);

    this._onBufferMetricsReadyListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onBufferMetricsAvailable,
    );
    this._onVODFragmentInfoAvailableListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlists.vodFragmentsInfo,
      this._onVODFragmentsInfoAvailable,
    );
  }

  @bind
  destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onBufferMetricsReadyListener);
    this._eventBus.unsubscribe(this._onVODFragmentInfoAvailableListener);
  }

  public setVideoElement(videoElement: HTMLVideoElement): void {
    this._videoElement = videoElement;
  }

  public removeVideoElement(): void {
    this._videoElement = null;
  }

  public _handleConfigUpdated(): void {}

  @bind
  private _onVODFragmentsInfoAvailable(fragments: IVODFragment[]): void {
    this._fragments = fragments.map(({ fragmentEndTS, fragmentStartTS }) => ({ startTs: fragmentStartTS, endTs: fragmentEndTS }));
  }

  @bind
  private _onBufferMetricsAvailable(metrics: IBufferMetrics): void {
    if (!this._fragments.length || !metrics.bufferedTimeRanges || this._videoElement?.paused) {
      return;
    }

    const isPlaybackPositionChanged: boolean = this._lastPlaybackPosition !== metrics.currentPlaybackPosition;
    this._lastPlaybackPosition = metrics.currentPlaybackPosition;

    if (!isPlaybackPositionChanged) {
      this._numberOfDetectedStallCandidates += 1;
    } else {
      this._numberOfDetectedStallCandidates = 0;
    }

    const isInGap: boolean = !metrics.bufferedTimeRanges.some(({ isCurrentTimeRange }) => isCurrentTimeRange);
    const inStuckInRange: boolean =
      !isPlaybackPositionChanged && this.config.numberOfCandidatesToDetectGap <= this._numberOfDetectedStallCandidates;

    if (isInGap || inStuckInRange) {
      // Find only gaps between (or in) two adjacent fragments

      const nextFragmentStartTS: number = this._fragments.find(({ startTs }) => startTs > metrics.currentPlaybackPosition)?.startTs || -1;
      const nextAvailableRangeStartTs: number =
        metrics.bufferedTimeRanges.find(({ start }) => start > metrics.currentPlaybackPosition)?.start || -1;

      if (nextFragmentStartTS === nextAvailableRangeStartTs && this._videoElement) {
        this._videoElement.currentTime = nextAvailableRangeStartTs + 0.01;
        this._numberOfDetectedStallCandidates = 0;
      }
    }
  }
}
